import Accordion from "@components/Accordion/Accordion";
import HeroButton from "@components/HeroButton";
import ImageGrid from "@components/ImageGrid";
import CardDataCarousal from "@components/LandingPageComponents/CardDataCarousal";
import { CardDataDetailsProps } from "@components/LandingPageComponents/CardDataDetails";
import FeatureCardSection from "@components/LandingPageComponents/CardSection";
import TestimonialSection from "@components/LandingPageComponents/TestimonialSection";
import ClientLogos from "@components/new-ui/ClientLogos";
import NewLayout from "@components/new-ui/Layout";
import YellowCTAhref from "@components/yellowCTAhref";
import Head from "next/head";
import Image from "next/image";
import React from "react";

const cardDataCollection: CardDataDetailsProps[] = [
    {
        img: "https://assets.gallabox.com/gb-home/hubspot-sync-contacts.svg",
        title: "Sync contacts and deals",
        description: "Instantly sync your HubSpot contacts and deals and engage them promptly by incorporating WhatsApp Business campaigns into your HubSpot workflows.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/hubspot-send-personalized.svg",
        title: "Send personalized WhatsApp messages",
        description: "Send personalized messages enabling efficient sales and customer service. It also improves visibility and efficiency across the customer lifecycle.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/hubspot-trigger-events.svg",
        title: "Trigger event-based messages",
        description: "Automatically trigger messages within your HubSpot workflows based on specific customer actions and events.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/hubspot-track-customer.svg",
        title: "Track customer engagement",
        description: "Track WhatsApp messages, sends, opens, and clicks in Gallabox for all your HubSpot contacts and deals. Ensure no opportunity falls through the cracks.",
    },
];

const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
        {
            "@type": "Question",
            name: "Do I need coding knowledge for HubSpot WhatsApp Integration?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "No! You can integrate HubSpot and WhatsApp in minutes with just a few clicks. In no time, you can send personalized WhatsApp messages and streamline communication for all your HubSpot contacts.",
            },
        },
        {
            "@type": "Question",
            name: "What type of campaigns can I run with WhatsApp marketing automation?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "By integrating Gallabox and HubSpot workflow, you can run various WhatsApp marketing automation campaigns, such as event-based triggers, personalized messages, follow-ups, payment notifications, and more, enhancing customer engagement and revenue.",
            },
        },
        {
            "@type": "Question",
            name: "Can I customize WhatsApp messages for HubSpot users?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Absolutely! HubSpot workflows allow you to create custom workflows to configure triggers and actions for your HubSpot contacts with tailored WhatsApp campaigns.",
            },
        },
        {
            "@type": "Question",
            name: "Is WhatsApp for HubSpot integration secure?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Yes! Integrating HubSpot with Gallabox is a secure option to run WhatsApp campaigns for HubSpot contacts. Gallabox and HubSpot use encryption and follow security protocols and industry best practices to ensure your data is secured while being stored or transferred.",
            },
        },
    ],
};

const hubSpotLanding: React.FC = () => {
    return (
        <NewLayout
            title="HubSpot WhatsApp Integration for Engagement | Gallabox"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/hubspot-og.png" }}
            canonical="https://gallabox.com/whatsapp-integrations/hubspot"
            description="Connect WhatsApp with HubSpot using Gallabox’s HubSpot WhatsApp integration. Automate CRM chats and close leads faster."
        >
            <Head>
                <script type="application/ld+json">{JSON.stringify(faqPageSchema)}</script>
            </Head>

            <div className="bg-white">
                <header className="max-w-6xl mx-auto px-4 lg:px-0  bg-white ">
                    <div className="flex flex-col lg:flex-row justify-center md:justify-between lg:py-28 pt-0 mt-10">
                        <div className="lg:w-1/2 w-full flex flex-col lg:items-start items-center">
                            <div
                                className="text-green-700 bg-green-200 wati-top-green justify-center items-center lg:text-base font-bold flex flex-nowrap w-fit m-auto md:m-0"
                                style={{ padding: "5px 12px", borderRadius: "50px", border: "0.5px solid rgb(9, 146, 80)" }}
                            >
                                <p className=" lg:text-base text-xs">Hubspot WhatsApp Integration</p>
                                <span className="wa-control-img">
                                    <img
                                        height="24px"
                                        width="24px"
                                        src="https://assets.gallabox.com/gb-home/new-zapier/wa-w-whiteborder.svg"
                                        alt="white-border-whatsapp-icon"
                                        className="ml-2"
                                    />
                                </span>
                            </div>
                            <h1 className="md:text-4xl text-2xl text-center md:text-left text-gray-500 mt-10">HubSpot WhatsApp Integration for Improved Customer Engagement</h1>
                            <p className="lg:py-10 py-4 lg:text-xl font-normal text-center lg:text-left lg:text-gray-700 text-gray-600">
                                Gallabox ensures you never drop the ball on customer communication and meet them where they are.
                            </p>
                            <HeroButton buttontext="SignUp for Free" />
                            <div className="flex gap-4 px-2 lg:px-0 mt-6">
                                {["No credit card required", "No setup fee"].map((each) => {
                                    return (
                                        <div className="flex items-center gap-1 md:gap-2" key={each}>
                                            <img src="https://assets.gallabox.com/gb-home/content-with-image/grey-tick-circle.svg" alt="Gray checkmark" className="opacity-50" />
                                            <p className="head-points whitespace-nowrap">{each}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="lg:w-1/2 w-full">
                            <Image className="w-full h-full" width={576} height={460} src="https://assets.gallabox.com/gb-home/hubspot-head-comp.svg" alt="hubsopt" />
                        </div>
                    </div>
                </header>

                <div className="lg:max-w-7xl mx-auto client-logos">
                    <h3 className="text-base text-center font-normal py-2">Trusted by 10,000+ businesses</h3>
                    <ClientLogos />
                </div>

                <CardDataCarousal cardDataCollection={cardDataCollection} />

                <div className="mt-20">
                    <FeatureCardSection
                        subTitle="No code setup"
                        title="No set-up time. One click HubSpot sync."
                        imageURl="https://assets.gallabox.com/gb-home/hubspot-no-code.svg"
                        description="Sync HubSpot accounts and deals with Gallabox and streamline your WhatsApp customer engagement workflow. The predefined templates with module fields help you reach prospects and customers with the right message based on customer actions and events. Save time and improve your productivity."
                        isImageRightSide
                    />
                </div>
                <FeatureCardSection
                    subTitle="Shared Team Inbox"
                    imageURl="https://assets.gallabox.com/gb-home/hubspot-shared-team.svg"
                    title="Democratize customer context. Improve customer experience."
                    description="Integrating HubSpot with Gallabox means no more context-switching between the two apps. And with Gallabox’s team inbox, the improved data visibility enables multiple agents to take over conversations of all your HubSpot accounts without any context loss, offering a seamless customer experience."
                />
                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/hubspot-whatsapp-campaigns.svg"
                    subTitle="WhatsApp Campaigns & Reports"
                    title="Run surgically precise WhatsApp campaigns at scale."
                    description="Create segments of your HubSpot contacts and run focused campaigns with open rates north of 90% with recommended products, new launches, and more. Get real-time analytics to take action on time, every time."
                    isImageRightSide
                />
                <div className=" lg:pt-20 ">
                    <YellowCTAhref
                        customText={<h2 className="text-2xl md:text-3xl font-bold text-yellow-800">Connect Gallabox and HubSpot in minutes, no code required</h2>}
                        // link2="https://calendly.com/gallabox/get-personalized-walktrhough?utm_source=website&utm_medium=hubspot&utm_campaign=lp"
                    />
                </div>
                <div className="pt-20">
                    <TestimonialSection
                        TestimonialSectionPropsData={[
                            {
                                avatarImg: "https://assets.gallabox.com/gb-home/hubs-owner-1.png",
                                headerTitle:
                                    "Gallabox’s intuitive UI, seamless two-way integrations, pre-defined templates, and top-notch support make it the best WhatsApp Automation Platform for HubSpot CRM users.",
                                nameText: "Likhith R",
                                professionText: "Senior Marketer, Drapestory",
                            },
                            {
                                avatarImg: "https://assets.gallabox.com/gb-home/hubs-owner-2.png",
                                headerTitle:
                                    "Gallabox’s automation and integration capabilities especially Facebook and HubSpot integration coupled with its bot automation help increase conversion rates, reach customers in real-time, and improve brand presence.",
                                nameText: "Dheeraj Goyal",
                                professionText: "Creative Advisory board Member, Creative Public Sr Sec School",
                            },
                        ]}
                    />
                </div>

                <section className="w-full h-full pt-20" style={{ backgroundImage: "url(/Ellipse.svg)" }}>
                    <ImageGrid />
                </section>
                <section className="max-w-6xl py-4 px-4 lg:px-0 mx-auto pb-10">
                    <div style={{ borderRadius: "6px" }} className="w-full h-40  bg-yellow-200">
                        <div className="flex flex-col lg:gap-4 gap-2 justify-center items-center px-4 lg:px-0 lg:py-8 py-4">
                            <h2 className="text-yellow-800 font-bold text-center lg:text-3xl text-lg">Engage your HubSpot contacts on WhatsApp today</h2>
                            <a
                                className="main-button outline-none focus:outline-none start-trial-btn hover:no-underline hover:shadow-lg transition duration-300 ease-in-out inline-flex items-center justify-center md:justify-start gap-1"
                                target="_blank"
                                href="https://app.gallabox.com/signup"
                                rel="noreferrer"
                            >
                                Get started Today
                            </a>
                        </div>
                    </div>
                </section>
                <section className="max-w-6xl py-4 mx-auto px-4">
                    <div className="flex flex-col justify-center lg:gap-4 gap-2 items-center pb-6">
                        <p className="lg:text-4xl text-3xl text-center font-bold text-gray-700">Frequently asked questions</p>
                        <h3 className="text-gray-500 text-center lg:text-2xl text-lg font-normal ">Everything you need to know about the HubSpot-Gallabox integration</h3>
                    </div>

                    <Accordion
                        title="HubSpot Integration"
                        items={[
                            {
                                title: "Do I need coding knowledge for HubSpot WhatsApp Integration?",
                                description:
                                    "No! You can integrate HubSpot and WhatsApp in minutes with just a few clicks. In no time, you can send personalized WhatsApp messages and streamline communication for all your HubSpot contacts.",
                            },
                            {
                                title: "What type of campaigns can I run with WhatsApp marketing automation?",
                                description:
                                    "By integrating Gallabox and HubSpot workflow, you can run various WhatsApp marketing automation campaigns, such as event-based triggers, personalized messages, follow-ups, payment notifications, and more, enhancing customer engagement and revenue.",
                            },
                            {
                                title: "Can I customize WhatsApp messages for HubSpot users?",
                                description:
                                    "Absolutely! HubSpot workflows allow you to create custom workflows to configure triggers and actions for your HubSpot contacts with tailored WhatsApp campaigns.",
                            },
                            {
                                title: "Is WhatsApp for HubSpot integration secure?",
                                description:
                                    "Yes! Integrating HubSpot with Gallabox is a secure option to run WhatsApp campaigns for HubSpot contacts. Gallabox and HubSpot use encryption and follow security protocols and industry best practices to ensure your data is secured while being stored or transferred.",
                            },
                        ]}
                    />
                </section>
            </div>
        </NewLayout>
    );
};

export default hubSpotLanding;
